<template>
  <v-dialog
    v-model="isOpen"
    fullscreen
    transition="dialog-bottom-transition"
    scrollable
  >
    <v-card>
      <!-- Toolbar -->
      <v-toolbar dark color="primary" elevation="2">
        <v-toolbar-title class="text-h6 font-weight-bold">
          <v-icon start>mdi-table-large</v-icon>
          Renters/Tenants Analytics - Detailed Table View
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          icon
          @click="exportToExcel"
          title="Export to Excel"
          class="mr-2"
          variant="text"
        >
          <v-icon>mdi-microsoft-excel</v-icon>
        </v-btn>
        <v-btn icon @click="closeDialog" variant="text">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Loading State -->
      <v-overlay
        :model-value="loading"
        contained
        class="align-center justify-center"
      >
        <div class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
            class="mb-4"
          />
          <div class="text-h6 mb-2">Loading Analytics Data...</div>
          <div class="text-caption">
            Generating {{ stats.totalBookingIntervals?.toLocaleString() }} data points
          </div>
        </div>
      </v-overlay>

      <!-- Main Content -->
      <v-container v-show="!loading" fluid class="pa-4 bg-grey-lighten-4">
        <!-- Filters Section -->
        <v-card class="mb-4" elevation="2" rounded="lg">
          <v-card-text class="pa-4">
            <v-row align="center" dense>
              <v-col cols="12" sm="6" md="4" lg="2">
                <v-select
                  v-model="selectedYear"
                  :items="availableYears"
                  label="Year"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-calendar"
                  clearable
                  hide-details
                  @update:model-value="onYearChange"
                />
              </v-col>
              
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-select
                  v-model="selectedMonth"
                  :items="availableMonths"
                  item-value="value"
                  item-title="text"
                  label="Month"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-calendar-month"
                  clearable
                  hide-details
                  :disabled="!selectedYear"
                  @update:model-value="onMonthChange"
                />
              </v-col>
              
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-select
                  v-model="selectedDay"
                  :items="availableDays"
                  item-value="value"
                  item-title="text"
                  label="Day"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-calendar-today"
                  clearable
                  hide-details
                  :disabled="!selectedMonth"
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="2">
                <v-select
                  v-model="itemsPerPage"
                  :items="rowsPerPageOptions"
                  label="Rows"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-table-row"
                  hide-details
                />
              </v-col>
              
              <v-col cols="12" md="4" lg="2">
                <v-btn
                  color="error"
                  variant="flat"
                  size="default"
                  block
                  @click="clearFilters"
                  :disabled="!hasActiveFilters"
                  prepend-icon="mdi-filter-off"
                  rounded="lg"
                >
                  Reset
                </v-btn>
              </v-col>
            </v-row>

            <!-- Filter Chips -->
            <v-row v-if="hasActiveFilters || selectedYear" class="mt-2">
              <v-col cols="12">
                <div class="d-flex flex-wrap ga-2">
                  <v-chip 
                    size="small" 
                    color="primary" 
                    variant="flat"
                  >
                    <v-icon start size="small">mdi-database</v-icon>
                    {{ totalDataPoints.toLocaleString() }} records
                  </v-chip>
                  <v-chip 
                    v-if="selectedYear" 
                    size="small" 
                    closable 
                    @click:close="onYearChange(null)"
                    variant="tonal"
                  >
                    Year: {{ selectedYear }}
                  </v-chip>
                  <v-chip 
                    v-if="selectedMonth" 
                    size="small" 
                    closable 
                    @click:close="onMonthChange(null)"
                    variant="tonal"
                  >
                    {{ availableMonths.find(m => m.value === selectedMonth)?.text }}
                  </v-chip>
                  <v-chip 
                    v-if="selectedDay" 
                    size="small" 
                    closable 
                    @click:close="selectedDay = null"
                    variant="tonal"
                  >
                    {{ formatDateChip(selectedDay) }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- KPI Selection -->
        <v-card class="mb-4" elevation="2" rounded="lg">
          <v-card-text class="pa-4">
            <div class="text-subtitle-2 font-weight-bold mb-3 text-medium-emphasis d-flex align-center">
              <v-icon size="20" class="mr-2">mdi-chart-box-outline</v-icon>
              Select KPI to Display
            </div>
            <v-chip-group
              v-model="selectedKPI"
              selected-class="text-white"
              mandatory
              column
            >
              <v-row dense>
                <v-col cols="12" sm="6" md="4" lg="auto">
                  <v-chip
                    value="bookingUtilization"
                    color="blue-darken-2"
                    size="large"
                    variant="flat"
                    class="w-100"
                  >
                    <v-icon start size="20">mdi-calendar-check</v-icon>
                    Booking Utilization
                  </v-chip>
                </v-col>

                <v-col cols="12" sm="6" md="4" lg="auto">
                  <v-chip
                    value="energyPerBooking"
                    color="orange-darken-2"
                    size="large"
                    variant="flat"
                    class="w-100"
                  >
                    <v-icon start size="20">mdi-flash</v-icon>
                    Energy per Booking
                  </v-chip>
                </v-col>

                <v-col cols="12" sm="6" md="4" lg="auto">
                  <v-chip
                    value="comfortSatisfaction"
                    color="green-darken-2"
                    size="large"
                    variant="flat"
                    class="w-100"
                  >
                    <v-icon start size="20">mdi-emoticon-happy</v-icon>
                    Comfort Satisfaction
                  </v-chip>
                </v-col>

                <v-col cols="12" sm="6" md="4" lg="auto">
                  <v-chip
                    value="bookingDuration"
                    color="purple-darken-2"
                    size="large"
                    variant="flat"
                    class="w-100"
                  >
                    <v-icon start size="20">mdi-clock-outline</v-icon>
                    Booking Duration
                  </v-chip>
                </v-col>

                <v-col cols="12" sm="6" md="4" lg="auto">
                  <v-chip
                    value="co2Awareness"
                    color="teal-darken-2"
                    size="large"
                    variant="flat"
                    class="w-100"
                  >
                    <v-icon start size="20">mdi-leaf</v-icon>
                    CO₂ Awareness
                  </v-chip>
                </v-col>
              </v-row>
            </v-chip-group>
          </v-card-text>
        </v-card>

        <!-- Warning for large datasets -->
        <v-alert
          v-if="totalDataPoints > 5000"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
          rounded="lg"
        >
          <template v-slot:prepend>
            <v-icon size="20">mdi-alert</v-icon>
          </template>
          <span class="text-body-2">
            Large dataset ({{ totalDataPoints.toLocaleString() }} records). 
            Use pagination to navigate efficiently. Maximum {{ maxRowsPerPage }} rows per page.
          </span>
        </v-alert>

        <!-- Data Table -->
        <v-card elevation="3" rounded="lg">
          <v-card-title class="bg-primary text-white py-4 px-4">
            <v-row align="center" dense>
              <v-col cols="12" md="8">
                <div class="d-flex align-center">
                  <v-icon start size="24" class="mr-2">mdi-table</v-icon>
                  <span class="text-h6 font-weight-bold">
                    {{ getKPITitle(selectedKPI) }} - 15 Minute Intervals
                  </span>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-md-right">
                <v-chip 
                  color="white" 
                  variant="flat" 
                  size="small"
                >
                  Total: {{ filteredTableData.length.toLocaleString() }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-title>

          <v-divider />

          <v-data-table
            :headers="tableHeaders"
            :items="filteredTableData"
            :items-per-page="itemsPerPage"
            :items-per-page-options="itemsPerPageOptionsForTable"
            density="comfortable"
            :loading="loading"
            fixed-header
            :height="tableHeight"
            hover
          >
            <!-- Date Column -->
            <template v-slot:[`item.date`]="{ item }">
              <div>
                <div class="text-body-2 font-weight-bold text-primary">
                  {{ formatDateDisplay(item.date) }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ item.dayOfWeek }}
                </div>
              </div>
            </template>

            <!-- Time Column -->
            <template v-slot:[`item.time`]="{ item }">
              <v-chip 
                size="small" 
                variant="tonal" 
                color="primary"
              >
                <v-icon start size="16">mdi-clock-outline</v-icon>
                {{ item.time }}
              </v-chip>
            </template>

            <!-- Main Value Column -->
            <template v-slot:[`item.mainValue`]="{ item }">
              <v-chip
                :color="getValueColor(item.mainValue)"
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                {{ formatMainValue(item.mainValue) }}
              </v-chip>
            </template>

            <!-- Secondary Value Column -->
            <template v-slot:[`item.secondaryValue`]="{ item }">
              <span class="text-body-2 font-weight-medium">
                {{ item.secondaryValue }}
              </span>
            </template>

            <!-- Additional Info Column -->
            <template v-slot:[`item.additionalInfo`]="{ item }">
              <div class="text-caption text-medium-emphasis">
                {{ item.additionalInfo }}
              </div>
            </template>

            <!-- Loading -->
            <template v-slot:loading>
              <v-skeleton-loader type="table-row@10" />
            </template>

            <!-- No data -->
            <template v-slot:no-data>
              <div class="text-center pa-12">
                <v-icon size="64" color="grey-lighten-2" class="mb-4">
                  mdi-table-off
                </v-icon>
                <div class="text-h6 mb-2 text-medium-emphasis">
                  No data available
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  Adjust your filters to see results
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { 
  getDetailedRentersDataStats,
  getAvailableRentersYears,
  getAvailableRentersMonths,
  getAvailableRentersDays,
  getAllRentersBookings,
  filterDetailedRentersData
} from './detailedRentersTenantData.js';
import { exportRentersTenantsDetailedToExcel } from './exportRentersTenantsDetailed.js';

// Props & Emits
const props = defineProps({
  modelValue: { type: Boolean, default: false }
});
const emit = defineEmits(['update:modelValue']);

// Dialog state
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// State
const loading = ref(false);
const selectedKPI = ref('bookingUtilization');
const selectedYear = ref(null);
const selectedMonth = ref(null);
const selectedDay = ref(null);
const itemsPerPage = ref(200);
const stats = ref({});

// Constants
const maxRowsPerPage = 2000;
const rowsPerPageOptions = [50, 100, 200, 500, 1000, 2000];

const itemsPerPageOptionsForTable = computed(() => [
  { value: 50, title: '50' },
  { value: 100, title: '100' },
  { value: 200, title: '200' },
  { value: 500, title: '500' },
  { value: 1000, title: '1000' },
  { value: 2000, title: '2000' }
]);

// Responsive table height
const tableHeight = computed(() => {
  if (typeof window !== 'undefined') {
    const height = window.innerHeight;
    if (height < 768) return 'calc(100dvh - 500px)';
    if (height < 1024) return 'calc(100dvh - 480px)';
    return 'calc(100dvh - 450px)';
  }
  return 'calc(100dvh - 450px)';
});

// Available options
const availableYears = ref([]);
const availableMonths = ref([]);
const availableDays = ref([]);

// All bookings data
const allBookingsData = ref([]);

// Watch dialog open
watch(isOpen, async (newValue) => {
  if (newValue) {
    await initializeData();
  }
});

// Initialize data
const initializeData = async () => {
  loading.value = true;
  
  try {
    await new Promise(resolve => setTimeout(resolve, 800));
    
    stats.value = getDetailedRentersDataStats();
    availableYears.value = getAvailableRentersYears();
    
    const currentYear = new Date().getFullYear();
    if (availableYears.value.includes(currentYear)) {
      selectedYear.value = currentYear;
      availableMonths.value = getAvailableRentersMonths(currentYear);
    }
    
    allBookingsData.value = getAllRentersBookings();
  } catch (error) {
    console.error('Error loading data:', error);
  } finally {
    loading.value = false;
  }
};

// Filter handlers
const onYearChange = (year) => {
  selectedMonth.value = null;
  selectedDay.value = null;
  availableMonths.value = year ? getAvailableRentersMonths(year) : [];
  availableDays.value = [];
};

const onMonthChange = (month) => {
  selectedDay.value = null;
  availableDays.value = month ? getAvailableRentersDays(month) : [];
};

const clearFilters = () => {
  const currentYear = new Date().getFullYear();
  selectedYear.value = currentYear;
  selectedMonth.value = null;
  selectedDay.value = null;
  availableMonths.value = getAvailableRentersMonths(currentYear);
  availableDays.value = [];
};

const hasActiveFilters = computed(() => {
  const currentYear = new Date().getFullYear();
  return selectedYear.value !== currentYear || selectedMonth.value || selectedDay.value;
});

const closeDialog = () => {
  isOpen.value = false;
};

// Get KPI title
const getKPITitle = (kpi) => {
  const titles = {
    bookingUtilization: 'Booking Utilization',
    energyPerBooking: 'Energy per Booking',
    comfortSatisfaction: 'Comfort Satisfaction',
    bookingDuration: 'Booking Duration',
    co2Awareness: 'CO₂ Awareness'
  };
  return titles[kpi] || 'Analytics';
};

// Table headers - Dynamic based on KPI
const tableHeaders = computed(() => {
  const baseHeaders = [
    { title: 'Date', key: 'date', sortable: true, width: '140px' },
    { title: 'Time', key: 'time', sortable: true, width: '100px' },
  ];

  switch(selectedKPI.value) {
    case 'bookingUtilization':
      return [
        ...baseHeaders,
        { title: 'Utilization (%)', key: 'mainValue', sortable: true, width: '130px' },
        { title: 'Booked Slots', key: 'secondaryValue', sortable: true, width: '120px' },
        { title: 'Details', key: 'additionalInfo', sortable: false, minWidth: '200px' }
      ];
    
    case 'energyPerBooking':
      return [
        ...baseHeaders,
        { title: 'Energy (kWh)', key: 'mainValue', sortable: true, width: '130px' },
        { title: 'Cost (€)', key: 'secondaryValue', sortable: true, width: '120px' },
        { title: 'Space Breakdown', key: 'additionalInfo', sortable: false, minWidth: '250px' }
      ];
    
    case 'comfortSatisfaction':
      return [
        ...baseHeaders,
        { title: 'Comfort Score (%)', key: 'mainValue', sortable: true, width: '150px' },
        { title: 'Energy Analytics', key: 'secondaryValue', sortable: true, width: '140px' },
        { title: 'Status', key: 'additionalInfo', sortable: false, width: '180px' }
      ];
    
    case 'bookingDuration':
      return [
        ...baseHeaders,
        { title: 'Avg Duration (hrs)', key: 'mainValue', sortable: true, width: '150px' },
        { title: 'Active Bookings', key: 'secondaryValue', sortable: true, width: '140px' },
        { title: 'Efficiency', key: 'additionalInfo', sortable: false, width: '150px' }
      ];
    
    case 'co2Awareness':
      return [
        ...baseHeaders,
        { title: 'Awareness Score (%)', key: 'mainValue', sortable: true, width: '160px' },
        { title: 'Emissions (kg)', key: 'secondaryValue', sortable: true, width: '140px' },
        { title: 'Actions', key: 'additionalInfo', sortable: false, width: '180px' }
      ];
    
    default:
      return baseHeaders;
  }
});

// Filtered table data
const filteredTableData = computed(() => {
  let data = [...allBookingsData.value];
  
  if (selectedYear.value) {
    data = data.filter(item => item.year === selectedYear.value);
  }
  if (selectedMonth.value) {
    data = data.filter(item => item.month === selectedMonth.value);
  }
  if (selectedDay.value) {
    data = data.filter(item => item.date === selectedDay.value);
  }
  
  // Transform based on selected KPI
  return data.map(item => {
    const baseData = {
      date: item.date,
      dayOfWeek: item.dayOfWeek,
      time: item.time,
      month: item.month,
      year: item.year
    };
    
    switch (selectedKPI.value) {
      case 'bookingUtilization':
        return {
          ...baseData,
          mainValue: item.utilizationRate,
          secondaryValue: `${item.bookedSlots} / 18`,
          additionalInfo: `Available: ${18 - item.bookedSlots} slots`
        };
        
      case 'energyPerBooking':
        const energyKwh = Math.round((10 + Math.random() * 10) * 10) / 10;
        const cost = (energyKwh * 0.28).toFixed(2);
        return {
          ...baseData,
          mainValue: energyKwh,
          secondaryValue: `€${cost}`,
          additionalInfo: `Meeting: 42% | Conference: 28% | Office: 20% | Breakout: 10%`
        };
        
      case 'comfortSatisfaction':
        const comfortScore = Math.round((70 + Math.random() * 20) * 10) / 10;
        const energyAnalytics = Math.round(comfortScore * 0.92 * 10) / 10;
        return {
          ...baseData,
          mainValue: comfortScore,
          secondaryValue: `${energyAnalytics}%`,
          additionalInfo: comfortScore >= 80 ? '✓ Excellent' : comfortScore >= 70 ? '⚠ Good' : '✗ Needs Improvement'
        };
        
      case 'bookingDuration':
        const duration = (Math.random() * 3 + 1).toFixed(1);
        const activeBookings = Math.round(item.bookedSlots * 0.8);
        const efficiency = Math.round((75 + Math.random() * 20) * 10) / 10;
        return {
          ...baseData,
          mainValue: parseFloat(duration),
          secondaryValue: `${activeBookings}`,
          additionalInfo: `Efficiency: ${efficiency}%`
        };
        
      case 'co2Awareness':
        const awarenessScore = Math.round((60 + Math.random() * 25) * 10) / 10;
        const emissions = Math.round((5 + Math.random() * 20) * 10) / 10;
        const actions = Math.floor(Math.random() * 4);
        return {
          ...baseData,
          mainValue: awarenessScore,
          secondaryValue: `${emissions}`,
          additionalInfo: `Positive actions: ${actions}`
        };
        
      default:
        return baseData;
    }
  });
});

// Total data points
const totalDataPoints = computed(() => filteredTableData.value.length);

// Format helpers
const formatDateDisplay = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
};

const formatDateChip = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
};

const formatMainValue = (value) => {
  switch (selectedKPI.value) {
    case 'bookingUtilization':
      return `${value}%`;
    case 'energyPerBooking':
      return `${value} kWh`;
    case 'comfortSatisfaction':
      return `${value}%`;
    case 'bookingDuration':
      return `${value} hrs`;
    case 'co2Awareness':
      return `${value}%`;
    default:
      return value;
  }
};

const getValueColor = (value) => {
  if (selectedKPI.value === 'bookingUtilization' || selectedKPI.value === 'comfortSatisfaction' || selectedKPI.value === 'co2Awareness') {
    if (value >= 80) return 'success';
    if (value >= 60) return 'warning';
    return 'error';
  }
  return 'primary';
};

// Export
const exportToExcel = async () => {
  loading.value = true;
  
  try {
    const filters = {};
    if (selectedYear.value) filters.year = selectedYear.value;
    if (selectedMonth.value) filters.month = selectedMonth.value;
    if (selectedDay.value) filters.day = selectedDay.value;
    
    const result = await exportRentersTenantsDetailedToExcel(filters);
    
    if (result.success) {
      console.log('Export successful:', result.filename);
    } else {
      console.error('Export failed:', result.error);
    }
    
  } catch (error) {
    console.error('Export error:', error);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.w-100 {
  width: 100% !important;
}
</style>